import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FarmlyAvatar } from '@/components/farmly/FarmlyAvatar';
import { useMyProfile, useUpsertMyProfile, useClearMyProfileField } from '@/hooks/useMyProfile';
import { uploadUserAvatar } from '@/lib/storage/userAvatars';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { Loader2, Upload, X } from 'lucide-react';

export function ProfileIdentitySection() {
  const { user } = useAuth();
  const { profile, isLoading } = useMyProfile();
  const upsert = useUpsertMyProfile();
  const clearField = useClearMyProfileField();

  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Derive current values (local state overrides server)
  const currentName = displayName ?? profile?.display_name ?? '';
  const currentAvatar = avatarPreview ?? profile?.avatar_url ?? null;
  const initials = user?.email?.substring(0, 2).toUpperCase() ?? 'AN';

  const hasNameChanges =
    displayName !== null && displayName !== (profile?.display_name ?? '');

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    setUploading(true);
    try {
      const result = await uploadUserAvatar(user.id, file);
      setAvatarPreview(result.publicUrl);
      await upsert.mutateAsync({ avatar_url: result.publicUrl });
      track('profile_avatar_uploaded', { success: true });
      toast.success('Profilbild uppladdad');
    } catch (err: any) {
      track('profile_avatar_uploaded', { success: false });
      toast.error(err.message || 'Kunde inte ladda upp bilden');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        display_name: displayName ?? undefined,
      });
      track('profile_identity_saved', {
        has_display_name: !!(displayName || profile?.display_name),
        has_avatar: !!(avatarPreview || profile?.avatar_url),
      });
      toast.success('Sparat');
      setDisplayName(null);
      setAvatarPreview(null);
    } catch {
      toast.error('Kunde inte spara');
    }
  };

  const handleClearAvatar = async () => {
    try {
      await clearField.mutateAsync('avatar_url');
      setAvatarPreview(null);
      track('profile_identity_cleared', { field: 'avatar' });
      toast.success('Profilbild borttagen');
    } catch {
      toast.error('Kunde inte ta bort profilbild');
    }
  };

  const handleClearName = async () => {
    try {
      await clearField.mutateAsync('display_name');
      setDisplayName(null);
      track('profile_identity_cleared', { field: 'display_name' });
      toast.success('Visningsnamn borttaget');
    } catch {
      toast.error('Kunde inte rensa visningsnamn');
    }
  };

  if (isLoading) {
    return (
      <Card className="border-border/60 shadow-none animate-pulse">
        <CardContent className="p-5 h-32" />
      </Card>
    );
  }

  const isRemoteAvatar = currentAvatar?.startsWith('http');

  return (
    <Card className="border-border/60 shadow-none">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">
          Din identitet i Farmly
          <span className="text-muted-foreground font-normal ml-1">(valfritt)</span>
        </CardTitle>
        <p className="text-xs text-muted-foreground/60 mt-1">
          Hämtas automatiskt om du loggar in med Google, men du bestämmer själv.
          Visas inte på publika sidor.
        </p>
      </CardHeader>
      <CardContent className="p-5 pt-0 space-y-5">
        {/* Avatar */}
        <div className="flex items-center gap-4">
          {isRemoteAvatar ? (
            <img
              src={currentAvatar!}
              alt="Profilbild"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              className="h-16 w-16 rounded-full border border-[#D8D2CA] object-cover"
            />
          ) : (
            <FarmlyAvatar
              imageUrl={currentAvatar}
              initials={initials}
              size="lg"
            />
          )}
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                {uploading ? 'Laddar upp…' : 'Byt profilbild'}
              </Button>
              {currentAvatar && (
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={clearField.isPending}
                  onClick={handleClearAvatar}
                  className="text-muted-foreground"
                >
                  <X className="h-4 w-4 mr-1" />
                  Ta bort
                </Button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={handleAvatarSelect}
            />
            <p className="text-[10px] text-muted-foreground/50">
              JPG, PNG eller WebP. Max 2 MB.
            </p>
          </div>
        </div>

        {/* Display name */}
        <div className="space-y-1.5">
          <Label htmlFor="display-name" className="text-xs">
            Visningsnamn
          </Label>
          <div className="flex gap-2">
            <Input
              id="display-name"
              placeholder="T.ex. Andreas"
              value={currentName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
              className="flex-1"
            />
            {profile?.display_name && (
              <Button
                variant="ghost"
                size="sm"
                disabled={clearField.isPending}
                onClick={handleClearName}
                className="text-muted-foreground shrink-0"
              >
                <X className="h-4 w-4 mr-1" />
                Rensa
              </Button>
            )}
          </div>
        </div>

        {/* Save */}
        <Button
          size="sm"
          disabled={!hasNameChanges || upsert.isPending}
          onClick={handleSave}
        >
          {upsert.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : null}
          Spara
        </Button>
      </CardContent>
    </Card>
  );
}
